
export const formatVersionDate = (version: string | null) => {
  if (!version) return '';
  
  try {
    // Extract date parts from version string in YYYYMMDD format
    const year = version.slice(0, 4);
    const month = parseInt(version.slice(4, 6), 10);
    const day = parseInt(version.slice(6, 8), 10);
    
    // Check if the extracted parts make a valid date
    if (isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error(`Error formatting version date: ${version}`, error);
    return '';
  }
};

// Standardized version date formatter - consistent across all components
export const formatVersionDateStandard = (version: string | null): string => {
  if (!version || version.length !== 8 || !/^\d{8}$/.test(version)) {
    return '';
  }
  
  try {
    const year = version.slice(0, 4);
    const month = version.slice(4, 6);  
    const day = version.slice(6, 8);
    
    // Simple string formatting without Date object to avoid timezone issues
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error(`Error formatting version date: ${version}`, error);
    return '';
  }
};

// Alternative M/D/YYYY format
export const formatVersionDateUS = (version: string | null): string => {
  if (!version || version.length !== 8 || !/^\d{8}$/.test(version)) {
    return '';
  }

  try {
    const year = version.slice(0, 4);
    const month = parseInt(version.slice(4, 6), 10);
    const day = parseInt(version.slice(6, 8), 10);

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error(`Error formatting version date: ${version}`, error);
    return '';
  }
};

// Additional functions for VisualAssetEditor compatibility
export interface ImageVersion {
  version: string; // YYYYMMDD format
  url: string;
}

/**
 * Parse YYYYMMDD string to Date object
 */
export const parseVersionDate = (version: string): Date => {
  if (!version || typeof version !== 'string' || version.length !== 8) {
    console.warn('Invalid version format for parseVersionDate:', version);
    return new Date(); // Return current date as fallback
  }

  try {
    const year = parseInt(version.slice(0, 4), 10);
    const month = parseInt(version.slice(4, 6), 10) - 1; // Month is 0-indexed
    const day = parseInt(version.slice(6, 8), 10);

    const date = new Date(year, month, day);

    // Check if the parsed date is valid
    if (isNaN(date.getTime())) {
      console.warn('Failed to parse version date:', version);
      return new Date(); // Return current date as fallback
    }

    return date;
  } catch (error) {
    console.warn('Failed to parse version date:', version, error);
    return new Date(); // Return current date as fallback
  }
};

/**
 * Format version date for display in pills/labels (e.g., "July 31st, 2025")
 */
export const formatVersionLabel = (version: string): string => {
  try {
    if (!version) {
      return 'No version';
    }
    const date = parseVersionDate(version);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting version label:', version, error);
    return version || 'Invalid date';
  }
};

/**
 * Format version date for dropdown display (e.g., "2025-07-31")
 */
export const formatVersionDropdown = (version: string): string => {
  try {
    if (!version) {
      return 'No version';
    }
    return formatVersionDateStandard(version);
  } catch (error) {
    console.error('Error formatting version dropdown:', version, error);
    return version || 'Invalid date';
  }
};

/**
 * Sort image versions by date (newest first)
 */
export const sortVersionsByDate = (versions: ImageVersion[]): ImageVersion[] => {
  return [...versions].sort((a, b) => {
    const dateA = parseVersionDate(a.version);
    const dateB = parseVersionDate(b.version);
    return dateB.getTime() - dateA.getTime();
  });
};
